import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { useApp } from "../context/AppContext";
import {
  META,
  getCourse,
  completeModule,
  recordAnswer,
  getModuleProgress,
  getSubtopics,
  getLevelXP,
  getTodayCount,
  getGoal,
  saveCoursePosition,
  getCoursePosition,
  getLastChapter,
  getNextChapter,
  recordLastChapter,
  getNewlyEarnedBadges,
  markModuleStart,
  getModuleStart,
  getSectionAnsweredIds,
} from "../lib/store";
import { LESSONS } from "../data/lessons";
import { DEEPDIVES } from "../data/deepdives";
import { CHAPTER_THEORY } from "../data/chapter_theory";
import { InteractiveQuestion } from "../components/InteractiveQuestion";
import { RichText } from "../components/RichText";
import { Diagram } from "../components/Diagrams";
import {
  GraduationCap,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  BookOpenCheck,
  HelpCircle,
  Clock,
  Target,
  Sparkles,
  PlayCircle,
  Trophy,
  BookOpen,
  Star,
  RotateCcw,
  Award,
  Timer,
  X,
} from "lucide-react";

const BADGE_ICONS = { Footprints: Award, Flame: Award, BookCheck: BookOpenCheck, Target, Star, CalendarCheck: Award, GraduationCap, Trophy, Award };

/* ============================ Liste des modules ============================ */
function PathList() {
  const { level } = useApp();
  const cfg = META[level];
  const completedSet = useMemo(() => new Set(getCourse(level)), [level]); // eslint-disable-line react-hooks/exhaustive-deps

  const modules = cfg.sections.map((s, i) => {
    const mp = getModuleProgress(level, s.code);
    return { ...s, index: i, completed: completedSet.has(s.code), pct: mp.pct, mastered: mp.mastered, secTotal: mp.total };
  });
  const completedCount = modules.filter((m) => m.completed).length;
  const pct = Math.round((completedCount / modules.length) * 100);

  const last = getLastChapter(level);
  let resume = null;
  if (last && META[level].sections.some((s) => s.code === last.code)) {
    const chap = getSubtopics(level, last.code).find((c) => c.sub === last.sub);
    const mod = cfg.sections.find((s) => s.code === last.code);
    if (chap && mod) resume = { code: last.code, sub: last.sub, chapterTitle: chap.title, moduleName: mod.name };
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Parcours guidé · {cfg.label}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Formation</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Des modules découpés en petits chapitres de quelques minutes. Avancez à votre rythme, dans l'ordre que vous voulez.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 min-w-[180px]">
          <div className="font-mono text-3xl font-bold tabular">{pct}%</div>
          <div className="text-xs text-muted-foreground">Modules terminés · {completedCount}/{modules.length}</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden mt-2">
            <div className="h-full bg-primary transition-[width] rounded-full" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {resume && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Link
            to={`/formation/${resume.code}/${resume.sub}`}
            data-testid="resume-last-chapter"
            className="group flex items-center gap-4 rounded-2xl border-2 border-primary/50 bg-primary/10 p-5 transition-transform hover:-translate-y-0.5"
          >
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground shrink-0">
              <RotateCcw size={24} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary">Reprendre là où j'en étais</div>
              <div className="font-display font-bold text-lg leading-tight truncate">{resume.chapterTitle}</div>
              <div className="text-xs text-muted-foreground truncate">{resume.code} · {resume.moduleName}</div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shrink-0 group-hover:gap-3 transition-all">
              Reprendre <ChevronRight size={16} />
            </span>
          </Link>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((m, i) => {
          const lesson = LESSONS[level][m.code];
          return (
            <motion.div key={m.code} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}>
              <Link
                to={`/formation/${m.code}`}
                data-testid={`module-link-${m.code}`}
                className="group relative flex flex-col h-full rounded-2xl border border-border bg-card p-5 transition-[transform,box-shadow,border-color] hover:-translate-y-1 hover:shadow-lg hover:border-primary/50"
              >
                <div className="flex items-center gap-3">
                  <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0 font-mono font-bold ${m.completed ? "bg-success text-white" : "bg-primary/15 text-primary"}`}>
                    {m.completed ? <CheckCircle2 size={22} /> : i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-primary">{m.code}</span>
                      {m.completed && <span className="font-mono text-[10px] uppercase tracking-wider text-success">Terminé</span>}
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight truncate">{m.name}</h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2 flex-1">
                  {lesson ? lesson.tagline : `${m.count} questions officielles.`}
                </p>
                <div className="mt-4 flex items-center gap-3" data-testid={`module-progress-${m.code}`}>
                  <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-[width] ${m.pct >= 80 ? "bg-success" : "bg-primary"}`} style={{ width: `${m.pct}%` }} />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground tabular whitespace-nowrap">{m.pct}%</span>
                  <ChevronRight size={18} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ==================== Écran d'un module : liste de chapitres ==================== */
function ModuleChapters({ code }) {
  const { level } = useApp();
  const cfg = META[level];
  const sec = cfg.sections.find((s) => s.code === code);
  const lesson = LESSONS[level][code];
  const chapters = useMemo(() => getSubtopics(level, code), [level, code]);
  const mp = getModuleProgress(level, code);
  const doneChapters = chapters.filter((c) => c.done).length;
  const xp = getLevelXP(level);
  const goal = getGoal();
  const today = getTodayCount(level);
  const goalPct = Math.min(100, Math.round((today / goal) * 100));

  return (
    <div className="space-y-8 pb-10">
      <div>
        <Link to="/formation" data-testid="module-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft size={16} /> Tous les modules
        </Link>
        <div className="font-mono text-xs text-primary mt-3">{sec?.code}</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{sec?.name}</h1>
        {lesson && <p className="text-muted-foreground text-sm mt-2 max-w-2xl">{lesson.tagline}</p>}
      </div>

      {/* Bento : progression + objectif du jour */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Progression du module</div>
          <div className="font-mono text-3xl font-black tabular mt-2">{mp.pct}%</div>
          <div className="text-xs text-muted-foreground mt-1">{doneChapters}/{chapters.length} chapitres terminés</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden mt-3">
            <div className="h-full bg-primary rounded-full transition-[width]" style={{ width: `${mp.pct}%` }} />
          </div>
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 relative overflow-hidden" data-testid="objectif-du-jour">
          <div className="flex items-center gap-2 text-primary">
            <Target size={16} />
            <span className="text-xs font-bold uppercase tracking-[0.15em]">Objectif du jour</span>
          </div>
          <div className="font-mono text-3xl font-black tabular mt-2">{today}/{goal}</div>
          <div className="text-xs text-muted-foreground mt-1">questions aujourd'hui{goalPct >= 100 ? " · objectif atteint" : ""}</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden mt-3">
            <div className={`h-full rounded-full transition-[width] ${goalPct >= 100 ? "bg-success" : "bg-primary"}`} style={{ width: `${goalPct}%` }} />
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-warning">
            <Star size={16} />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Points</span>
          </div>
          <div className="font-mono text-3xl font-black tabular mt-2">{xp}</div>
          <div className="text-xs text-muted-foreground mt-1">+10 pts par question maîtrisée</div>
        </div>
      </div>

      {/* Le cours (leçons + schémas) */}
      {lesson && lesson.slides && lesson.slides.length > 0 && (
        <Link
          to={`/formation/${code}/cours`}
          data-testid="chapter-cours"
          className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:shadow-lg hover:border-primary/50"
        >
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/15 text-primary shrink-0">
            <BookOpen size={24} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg">Le cours & les schémas</div>
            <div className="text-sm text-muted-foreground">Les explications illustrées à lire avant les chapitres · {lesson.slides.length} leçons</div>
          </div>
          <ChevronRight size={20} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </Link>
      )}

      {/* Chapitres */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap size={18} className="text-primary" />
          <h2 className="font-display font-bold text-xl">Chapitres</h2>
          <span className="font-mono text-xs text-muted-foreground">({chapters.length})</span>
        </div>
        {(() => {
          const next = getNextChapter(level, code);
          if (!next) return null;
          const started = next.answered > 0;
          return (
            <Link
              to={`/formation/${code}/${next.sub}`}
              data-testid="chapter-recommended"
              className="group flex items-center gap-4 rounded-2xl border-2 border-primary/50 bg-primary/10 p-4 mb-4 transition-transform hover:-translate-y-0.5"
            >
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary text-primary-foreground shrink-0">
                {started ? <RotateCcw size={22} /> : <PlayCircle size={22} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary">Chapitre recommandé</div>
                <div className="font-display font-bold text-[15px] leading-tight truncate">{next.title}</div>
                <div className="text-xs text-muted-foreground">Chapitre {next.index + 1} · ~{next.minutes} min · {next.answered}/{next.total}</div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shrink-0 group-hover:gap-2.5 transition-all">
                {started ? "Reprendre" : "Commencer"} <ChevronRight size={16} />
              </span>
            </Link>
          );
        })()}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {chapters.map((c, i) => (
            <motion.div key={c.sub} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}>
              <Link
                to={`/formation/${code}/${c.sub}`}
                data-testid={`chapter-card-${c.sub}`}
                className="group flex flex-col h-full rounded-2xl border border-border bg-card p-4 transition-[transform,box-shadow,border-color] hover:-translate-y-1 hover:shadow-lg hover:border-primary/50"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={`grid place-items-center w-9 h-9 rounded-lg shrink-0 font-mono text-sm font-bold ${c.done ? "bg-success text-white" : c.answered > 0 ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {c.done ? <CheckCircle2 size={18} /> : i + 1}
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                    <Clock size={12} /> ~{c.minutes} min
                  </span>
                </div>
                <h3 className="font-display font-semibold text-[15px] leading-snug mt-3 flex-1">{c.title}</h3>
                <div className="mt-3 flex items-center gap-2">
                  <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-[width] ${c.done ? "bg-success" : "bg-primary"}`} style={{ width: `${c.total ? Math.round((c.answered / c.total) * 100) : 0}%` }} />
                  </div>
                  <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{c.answered}/{c.total}</span>
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-wider">
                  {c.done ? (
                    <span className="text-success">Terminé · +{c.mastered * 10} pts</span>
                  ) : c.answered > 0 ? (
                    <span className="text-primary">En cours</span>
                  ) : (
                    <span className="text-muted-foreground">Non commencé</span>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ==================== Écran festif de fin de module ==================== */
function ModuleCelebration({ data, code, onClose }) {
  const navigate = useNavigate();
  useEffect(() => {
    const fire = (opts) => confetti({ particleCount: 60, spread: 70, startVelocity: 45, ticks: 200, ...opts });
    fire({ origin: { x: 0.2, y: 0.7 }, angle: 60 });
    fire({ origin: { x: 0.8, y: 0.7 }, angle: 120 });
    const t1 = setTimeout(() => fire({ origin: { x: 0.5, y: 0.4 }, spread: 100, particleCount: 90 }), 250);
    const t2 = setTimeout(() => fire({ origin: { x: 0.5, y: 0.6 }, spread: 120, particleCount: 70 }), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const stats = [
    { icon: Star, label: "Points", value: `+${data.points}` },
    { icon: BookOpenCheck, label: "Maîtrisées", value: `${data.mastered}/${data.totalQ}` },
    { icon: GraduationCap, label: "Chapitres", value: data.chapters },
    { icon: Timer, label: "Temps", value: data.minutes ? `${data.minutes} min` : "—" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-background/80 backdrop-blur-sm p-4"
      data-testid="module-celebration"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="relative w-full max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-2xl"
      >
        <button onClick={onClose} data-testid="celebration-close" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
          <X size={20} />
        </button>
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
          className="inline-grid place-items-center w-24 h-24 rounded-full bg-warning/15 text-warning mx-auto"
          style={{ filter: "drop-shadow(0 0 14px hsl(var(--warning) / 0.4))" }}
        >
          <Trophy size={48} />
        </motion.div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-warning mt-5">Module terminé</div>
        <h2 className="font-display font-black text-3xl tracking-tight mt-1">Bravo !</h2>
        <p className="text-muted-foreground text-sm mt-2">Vous avez terminé « {data.moduleName} ».</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-secondary/40 p-3">
              <s.icon size={18} className="text-primary mx-auto" />
              <div className="font-mono font-black text-lg tabular mt-1">{s.value}</div>
              <div className="text-[11px] text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {data.badges && data.badges.length > 0 && (
          <div className="mt-6" data-testid="celebration-badges">
            <div className="font-mono text-[11px] uppercase tracking-wider text-warning mb-2">
              {data.badges.length > 1 ? "Nouveaux badges" : "Nouveau badge"}
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {data.badges.map((b) => {
                const Icon = BADGE_ICONS[b.icon] || Award;
                return (
                  <div key={b.id} className="inline-flex items-center gap-2 rounded-full border border-warning/40 bg-warning/10 px-3 py-1.5">
                    <Icon size={15} className="text-warning" />
                    <span className="text-sm font-semibold">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
          <button
            onClick={() => navigate(`/formation/${code}`)}
            data-testid="celebration-chapters"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-2.5 font-semibold hover:bg-secondary transition-colors"
          >
            Revoir les chapitres
          </button>
          <button
            onClick={() => navigate("/formation")}
            data-testid="celebration-modules"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-semibold hover:-translate-y-px transition-transform"
          >
            Continuer <ChevronRight size={16} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ==================== Vue d'un chapitre (questions) ==================== */
function ChapterView({ code, sub }) {
  const { level } = useApp();
  const navigate = useNavigate();
  const cfg = META[level];
  const sec = cfg.sections.find((s) => s.code === code);
  const chapters = useMemo(() => getSubtopics(level, code), [level, code]);
  const idx = chapters.findIndex((c) => c.sub === sub);
  const chapter = chapters[idx];
  const questions = chapter ? chapter.questions : [];

  const [answered, setAnswered] = useState(() => {
    const seed = {};
    if (chapter) {
      const done = new Set(getSectionAnsweredIds(level, code));
      for (const q of chapter.questions) if (done.has(q.id)) seed[q.id] = true;
    }
    return seed;
  });
  const [gained, setGained] = useState(0);
  const [celebration, setCelebration] = useState(null);
  const wasCompletedRef = useRef(getCourse(level).includes(code));

  const theory = (CHAPTER_THEORY[level] && CHAPTER_THEORY[level][sub]) || [];
  const [chapterPhase, setChapterPhase] = useState(theory.length ? "theory" : "questions");
  const [tSlide, setTSlide] = useState(0);

  useEffect(() => {
    if (chapter) {
      recordLastChapter(level, code, sub);
      markModuleStart(level, code);
    }
  }, [level, code, sub]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chapter) {
    return <ModuleChapters code={code} />;
  }

  const onAnswered = (isCorrect, q) => {
    recordAnswer(level, q.id, isCorrect);
    setAnswered((a) => ({ ...a, [q.id]: true }));
    if (isCorrect) setGained((g) => g + 10);

    // Vérifie complétion du module + badges (le store est déjà persisté).
    const chaptersNow = getSubtopics(level, code);
    const moduleDone = chaptersNow.length > 0 && chaptersNow.every((c) => c.done);

    if (moduleDone && !wasCompletedRef.current) {
      wasCompletedRef.current = true;
      if (!getCourse(level).includes(code)) completeModule(level, code);
      const newly = getNewlyEarnedBadges(level); // après completeModule → badges de module comptés
      const mp = getModuleProgress(level, code);
      const start = getModuleStart(level, code);
      const minutes = start ? Math.max(1, Math.round((Date.now() - start) / 60000)) : null;
      setCelebration({
        moduleName: sec?.name,
        points: mp.mastered * 10,
        mastered: mp.qMastered,
        totalQ: mp.qTotal,
        chapters: chaptersNow.length,
        minutes,
        badges: newly,
      });
    } else {
      const newly = getNewlyEarnedBadges(level);
      if (newly.length) newly.forEach((b) => toast.success(`Badge débloqué : ${b.label} !`, { description: b.desc }));
    }
  };

  const answeredCount = questions.filter((q) => answered[q.id]).length;
  const totalDone = answeredCount;
  const finished = questions.length > 0 && answeredCount === questions.length;
  const nextChapter = chapters[idx + 1];

  return (
    <div className="space-y-6 pb-10 max-w-3xl mx-auto">
      <AnimatePresence>
        {celebration && (
          <ModuleCelebration data={celebration} code={code} onClose={() => setCelebration(null)} />
        )}
      </AnimatePresence>
      <div>
        <Link to={`/formation/${code}`} data-testid="chapter-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft size={16} /> Chapitres du module
        </Link>
        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="font-mono text-xs text-primary">{sec?.code} · Chapitre {idx + 1}/{chapters.length}</div>
          <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
            <Clock size={12} /> ~{chapter.minutes} min
          </span>
        </div>
        <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1">{chapter.title}</h1>
      </div>

      {chapterPhase === "theory" ? (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-primary">Théorie · {tSlide + 1}/{theory.length}</span>
            <button onClick={() => setChapterPhase("questions")} data-testid="chapter-skip-theory" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Passer aux questions →
            </button>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-[width]" style={{ width: `${((tSlide + 1) / theory.length) * 100}%` }} />
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={tSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.22 }}
              data-testid={`theory-slide-${tSlide}`}
              className="rounded-2xl border border-border bg-card p-6 sm:p-8"
            >
              <div className="flex items-center gap-2 text-primary mb-3">
                <BookOpenCheck size={20} />
                <span className="font-mono text-xs uppercase tracking-wider">Théorie</span>
              </div>
              <h2 className="font-display font-black text-xl sm:text-2xl leading-tight">{theory[tSlide].title}</h2>
              {theory[tSlide].body && <p className="text-[15px] leading-relaxed mt-3"><RichText text={theory[tSlide].body} /></p>}
              {theory[tSlide].points && theory[tSlide].points.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {theory[tSlide].points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <RichText text={p} />
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center justify-between">
            <button
              disabled={tSlide === 0}
              onClick={() => setTSlide((x) => x - 1)}
              data-testid="theory-prev"
              className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2.5 text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
            >
              <ChevronLeft size={16} /> Précédent
            </button>
            {tSlide + 1 >= theory.length ? (
              <button
                onClick={() => setChapterPhase("questions")}
                data-testid="theory-to-questions"
                className="inline-flex items-center gap-2 rounded-full bg-success text-white px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform"
              >
                Passer aux questions <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={() => setTSlide((x) => x + 1)}
                data-testid="theory-next"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform"
              >
                Continuer <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {theory.length > 0 && (
            <button
              onClick={() => { setChapterPhase("theory"); setTSlide(0); }}
              data-testid="chapter-review-theory"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <BookOpenCheck size={14} /> Revoir la théorie
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-[width]" style={{ width: `${Math.round((totalDone / questions.length) * 100)}%` }} />
            </div>
            <span className="font-mono text-xs text-muted-foreground whitespace-nowrap" data-testid="chapter-counter">{totalDone}/{questions.length}</span>
          </div>

          <div className="space-y-3">
            {questions.map((q, i) => (
              <InteractiveQuestion key={q.id} q={q} index={i} onAnswered={onAnswered} testPrefix="chapter-q" />
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {chapterPhase === "questions" && finished && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="chapter-complete"
            className="rounded-2xl border border-success/40 bg-success/10 p-5 text-center space-y-3"
          >
            <div className="inline-grid place-items-center w-14 h-14 rounded-full bg-success/20 text-success">
              <Trophy size={28} />
            </div>
            <div className="font-display font-bold text-lg">Chapitre terminé !</div>
            {gained > 0 && (
              <div className="inline-flex items-center gap-1.5 font-mono text-sm text-success">
                <Sparkles size={15} /> +{gained} points
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
              {nextChapter ? (
                <button
                  onClick={() => navigate(`/formation/${code}/${nextChapter.sub}`)}
                  data-testid="chapter-next"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-semibold hover:-translate-y-px transition-transform"
                >
                  Chapitre suivant <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/formation/${code}`)}
                  data-testid="chapter-done-back"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-success text-white px-6 py-2.5 font-semibold hover:-translate-y-px transition-transform"
                >
                  Retour aux chapitres <CheckCircle2 size={16} />
                </button>
              )}
              <Link to={`/formation/${code}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-2.5 font-semibold hover:bg-secondary transition-colors">
                Voir tous les chapitres
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ==================== Le cours (leçons + schémas) ==================== */
function CoursePlayer({ code }) {
  const { level } = useApp();
  const navigate = useNavigate();
  const cfg = META[level];
  const sec = cfg.sections.find((s) => s.code === code);
  const lesson = LESSONS[level][code];
  const slides = lesson ? lesson.slides : [];

  const [slide, setSlide] = useState(() => {
    const pos = getCoursePosition(level, code);
    return pos && pos.slide ? Math.min(pos.slide, Math.max(0, slides.length - 1)) : 0;
  });
  const [deepOpen, setDeepOpen] = useState({});

  useEffect(() => {
    saveCoursePosition(level, code, { phase: "learn", slide });
  }, [slide, level, code]);

  const s = slides[slide];
  const deep = DEEPDIVES[code] ? DEEPDIVES[code][slide] : null;
  const last = slide + 1 >= slides.length;

  return (
    <div className="space-y-5 max-w-3xl mx-auto pb-10">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/formation/${code}`} data-testid="course-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft size={16} /> Chapitres du module
        </Link>
        <span className="font-mono text-xs text-muted-foreground">{sec?.code} · Cours</span>
      </div>

      <div>
        <div className="font-mono text-xs text-primary">{sec?.code}</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{sec?.name}</h1>
        {lesson && <p className="text-muted-foreground text-sm mt-1">{lesson.tagline}</p>}
      </div>

      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-[width]" style={{ width: `${((slide + 1) / slides.length) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={slide}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="rounded-2xl border border-border bg-card p-6 sm:p-8"
          data-testid={`course-slide-${slide}`}
        >
          <div className="flex items-center gap-2 text-primary mb-3">
            <BookOpenCheck size={20} />
            <span className="font-mono text-xs uppercase tracking-wider">Leçon {slide + 1}/{slides.length}</span>
          </div>
          <h2 className="font-display font-black text-2xl leading-tight">{s.title}</h2>
          <p className="text-[15px] leading-relaxed mt-4"><RichText text={s.body} /></p>
          {s.diagram && <Diagram name={s.diagram} section={code} level={level} />}
          {s.points && (
            <ul className="mt-4 space-y-2">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <RichText text={p} />
                </li>
              ))}
            </ul>
          )}
          {s.tip && (
            <div className="mt-5 flex items-start gap-2 rounded-xl bg-warning/10 border border-warning/30 px-3 py-2.5">
              <Lightbulb size={18} className="text-warning mt-0.5 shrink-0" />
              <RichText className="text-sm" text={s.tip} />
            </div>
          )}
          {deep && (
            <div className="mt-5 pt-4 border-t border-border">
              {!deepOpen[slide] ? (
                <button
                  onClick={() => setDeepOpen((o) => ({ ...o, [slide]: true }))}
                  data-testid="course-explain-more"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/40 text-primary px-4 py-2.5 text-sm font-semibold hover:bg-primary/10 transition-colors"
                >
                  <HelpCircle size={16} /> Je ne comprends pas bien cette leçon
                </button>
              ) : (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="course-deepdive" className="rounded-xl bg-primary/5 border border-primary/25 p-4">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                    <HelpCircle size={16} /> Explication détaillée
                  </div>
                  <p className="text-sm leading-relaxed"><RichText text={deep} /></p>
                </motion.div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between">
        <button
          disabled={slide === 0}
          onClick={() => setSlide((x) => x - 1)}
          data-testid="course-prev-slide"
          className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2.5 text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
        >
          <ChevronLeft size={16} /> Précédent
        </button>
        {last ? (
          <button
            onClick={() => navigate(`/formation/${code}`)}
            data-testid="course-finish-lesson"
            className="inline-flex items-center gap-2 rounded-full bg-success text-white px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform"
          >
            Passer aux chapitres <ChevronRight size={16} />
          </button>
        ) : (
          <button
            onClick={() => setSlide((x) => x + 1)}
            data-testid="course-next-slide"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform"
          >
            Continuer <ChevronRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ==================== Router interne ==================== */
export default function Formation() {
  const { code, sub } = useParams();
  const { level } = useApp();

  useEffect(() => {
    // Marque le module comme terminé quand tous ses chapitres sont finis.
    if (code && META[level].sections.some((s) => s.code === code)) {
      const chapters = getSubtopics(level, code);
      if (chapters.length && chapters.every((c) => c.done) && !getCourse(level).includes(code)) {
        completeModule(level, code);
      }
    }
  }, [code, sub, level]);

  const validCode = code && META[level].sections.some((s) => s.code === code);
  if (!validCode) return <PathList />;
  if (sub === "cours") return <CoursePlayer code={code} />;
  if (sub) return <ChapterView key={`${code}-${sub}`} code={code} sub={sub} />;
  return <ModuleChapters code={code} />;
}
